import { useCallback, useMemo, useState } from 'react';
import { Flex } from 'antd';
import type { WmsShipmentInboundType } from 'schemas/shipment/inbound/detail';
import { useGlobalContext } from '@contexts/global';
import {
  getEffectiveShipmentStatus,
  getInboundShipmentWarehouseId,
  getInboundWorkflowStepIndex,
  type InboundDetailDisplay,
} from '@utility/utils/shipment-display';
import { CommonUtility, ShipmentsService, ToastMessage } from '@utility/index';
import { InboundShipmentMainPanel } from './InboundShipmentMainPanel';
import { InboundShipmentSidebar } from './InboundShipmentSidebar';
import { getStorageAssignmentStats } from './InboundShipmentStorageAssignmentStep';
import { InboundMarkShipmentCompleteModal } from './InboundMarkShipmentCompleteModal';

type InboundShipmentDetailLayoutProps = {
  data: WmsShipmentInboundType;
  display: InboundDetailDisplay;
  loading?: boolean;
  summary?: { cartons: number; units: number; grossWeight: number };
  cbm?: number;
  onRefresh: () => void;
};

export function InboundShipmentDetailLayout({
  data,
  display,
  onRefresh,
}: InboundShipmentDetailLayoutProps) {
  const { warehouseId: globalWarehouseId } = useGlobalContext();
  const status = getEffectiveShipmentStatus(data);
  const stepIndex = Math.max(0, getInboundWorkflowStepIndex(status));
  const [mainTabKey, setMainTabKey] = useState('intake');
  const [markingArrived, setMarkingArrived] = useState(false);
  const [markingCompleted, setMarkingCompleted] = useState(false);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);

  const warehouseId = useMemo(
    () => getInboundShipmentWarehouseId(data, globalWarehouseId),
    [data, globalWarehouseId],
  );

  const storageStats = useMemo(
    () => getStorageAssignmentStats(data.pallets ?? []),
    [data.pallets],
  );

  const handleMarkArrived = useCallback(async () => {
    if (!warehouseId) {
      ToastMessage.error('Warehouse is required to update shipment status');
      return;
    }

    const payload = CommonUtility.removeEmptyFields({
      status: 'arrived',
      warehouseId,
    });

    try {
      setMarkingArrived(true);
      await ShipmentsService.changeStatus(data._id, payload);
      ToastMessage.success('Shipment marked as arrived');
      onRefresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setMarkingArrived(false);
    }
  }, [data._id, onRefresh, warehouseId]);

  const handleMarkCompleted = useCallback(
    async (discardUnusedPallets: boolean) => {
      if (!warehouseId) {
        ToastMessage.error('Warehouse is required to update shipment status');
        return;
      }

      const payload = CommonUtility.removeEmptyFields({
        status: 'completed',
        warehouseId,
        discardUnusedPallets,
      });

      try {
        setMarkingCompleted(true);
        await ShipmentsService.changeStatus(data._id, payload);
        ToastMessage.success('Shipment marked as completed');
        setCompleteModalOpen(false);
        onRefresh();
      } catch (error) {
        ToastMessage.apiError(error);
      } finally {
        setMarkingCompleted(false);
      }
    },
    [data._id, onRefresh, warehouseId],
  );

  const handleOpenCompleteModal = useCallback(() => {
    setCompleteModalOpen(true);
  }, []);

  return (
    <Flex gap={16} align="flex-start" wrap="wrap">
      <InboundShipmentMainPanel
        data={data}
        stepIndex={stepIndex}
        activeTabKey={mainTabKey}
        onTabChange={setMainTabKey}
        onMarkArrived={handleMarkArrived}
        markingArrived={markingArrived}
        onMarkCompleted={handleOpenCompleteModal}
        markingCompleted={markingCompleted}
      />
      <InboundShipmentSidebar data={data} display={display} onRefresh={onRefresh} />
      <InboundMarkShipmentCompleteModal
        open={completeModalOpen}
        onClose={() => setCompleteModalOpen(false)}
        unusedPalletCount={storageStats.withoutContentsCount}
        loading={markingCompleted}
        onConfirm={handleMarkCompleted}
      />
    </Flex>
  );
}
